import type { SessionVerification } from './gameSession'

export type QueuedScorePeriod = 'daily' | 'weekly'

export interface QueuedScorePayload {
  score: number
  period: QueuedScorePeriod
  sessionVerification?: SessionVerification
}

interface QueueEntry {
  id: string
  key: string
  payload: QueuedScorePayload
  enqueuedAt: number
  attempts: number
  lastAttemptAt: number | null
}

export interface QueueProcessResult {
  success: boolean
  permanent?: boolean
}

type QueueExecutor = (payload: QueuedScorePayload) => Promise<QueueProcessResult>

const QUEUE_KEY = 'ymg_score_queue_v1'
const TTL_MS = 7 * 24 * 60 * 60 * 1000
const MAX_ENTRIES = 100
const MAX_ATTEMPTS = 50
const BASE_BACKOFF_MS = 5_000
const MAX_BACKOFF_MS = 60_000

let processing = false

export function enqueueScoreSubmission(payload: QueuedScorePayload): boolean {
  const queue = readQueue()
  const key = buildKey(payload)
  if (queue.some((entry) => entry.key === key)) return false

  queue.push({
    id: createEntryId(),
    key,
    payload,
    enqueuedAt: Date.now(),
    attempts: 0,
    lastAttemptAt: null,
  })
  writeQueue(trimQueue(queue))
  return true
}

export async function processScoreQueue(executor: QueueExecutor): Promise<number> {
  if (processing) return 0
  processing = true
  let processed = 0

  try {
    while (true) {
      const queue = readQueue()
      const entry = queue[0]
      if (!entry) return processed
      if (!isDue(entry)) return processed

      const attempted: QueueEntry = {
        ...entry,
        attempts: entry.attempts + 1,
        lastAttemptAt: Date.now(),
      }
      writeQueue([attempted, ...queue.slice(1)])

      let result: QueueProcessResult
      try {
        result = await executor(attempted.payload)
      } catch (err) {
        console.warn('[scoreQueue] executor threw:', err)
        result = { success: false }
      }

      if (result.success || result.permanent || attempted.attempts >= MAX_ATTEMPTS) {
        removeById(attempted.id)
        if (result.success) processed += 1
        continue
      }

      return processed
    }
  } finally {
    processing = false
  }
}

export function hasQueuedScores(): boolean {
  return readQueue().length > 0
}

function readQueue(): QueueEntry[] {
  let parsed: unknown
  try {
    const raw = localStorage.getItem(QUEUE_KEY)
    parsed = raw ? JSON.parse(raw) : []
  } catch (err) {
    console.warn('[scoreQueue] failed to parse queue, resetting:', err)
    writeQueue([])
    return []
  }

  if (!Array.isArray(parsed)) {
    writeQueue([])
    return []
  }

  const cutoff = Date.now() - TTL_MS
  const valid = parsed.filter(isValidEntry).filter((entry) => entry.enqueuedAt >= cutoff)
  const trimmed = trimQueue(valid)
  if (trimmed.length !== parsed.length) writeQueue(trimmed)
  return trimmed
}

function writeQueue(queue: QueueEntry[]): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue))
  } catch (err) {
    console.warn('[scoreQueue] storage write failed:', err)
  }
}

function removeById(id: string): void {
  writeQueue(readQueue().filter((entry) => entry.id !== id))
}

function trimQueue(queue: QueueEntry[]): QueueEntry[] {
  if (queue.length <= MAX_ENTRIES) return queue
  return queue.slice(queue.length - MAX_ENTRIES)
}

function isDue(entry: QueueEntry): boolean {
  if (entry.lastAttemptAt === null || entry.attempts <= 0) return true
  const delay = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * 2 ** Math.min(entry.attempts - 1, 6))
  return Date.now() - entry.lastAttemptAt >= delay
}

function buildKey(payload: QueuedScorePayload): string {
  return [
    'score',
    payload.period,
    String(payload.score),
    payload.sessionVerification?.sessionId ?? '-',
  ].join(':')
}

function isValidEntry(value: unknown): value is QueueEntry {
  if (!value || typeof value !== 'object') return false
  const entry = value as Record<string, unknown>
  if (typeof entry.id !== 'string' || entry.id.length === 0) return false
  if (typeof entry.key !== 'string' || entry.key.length === 0) return false
  if (typeof entry.enqueuedAt !== 'number' || !Number.isFinite(entry.enqueuedAt)) return false
  if (typeof entry.attempts !== 'number' || !Number.isFinite(entry.attempts)) return false
  if (entry.lastAttemptAt !== null && typeof entry.lastAttemptAt !== 'number') return false

  const payload = entry.payload as Record<string, unknown> | undefined
  if (!payload || typeof payload !== 'object') return false
  if (typeof payload.score !== 'number' || !Number.isFinite(payload.score)) return false
  if (payload.period !== 'daily' && payload.period !== 'weekly') return false

  const verification = payload.sessionVerification
  if (verification === undefined) return true
  if (!verification || typeof verification !== 'object') return false
  const v = verification as Record<string, unknown>
  return (
    typeof v.sessionId === 'string' &&
    typeof v.seed === 'number' &&
    typeof v.durationMs === 'number' &&
    typeof v.actionCount === 'number' &&
    typeof v.actionsPerSecond === 'number' &&
    typeof v.finalScore === 'number'
  )
}

function createEntryId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  const rand = Math.random().toString(36).slice(2, 10)
  return `score_${Date.now().toString(36)}_${rand}`
}
